/**
 * Armazenamento no navegador (§2.4.4): três chaves em `sessionStorage` e, pela D3 (§8 de 18/10), uma em `localStorage`
 * (só o código IBGE do município escolhido); tudo em try/catch (modo privado e cota
 * cheia nunca quebram o app, AC-2.4-03). Nada pessoal. Único arquivo de src/ que toca o
 * armazenamento (FE §6.6).
 */
import type { IbgeCode } from '../contracts/base';

export const RELOAD_KEY_PREFIX = 'app:reload:';
export const TV_RELOADS_KEY = 'tv-reloads';
export const ONE_KEY_SHORTCUTS_KEY = 'atalhos-uma-tecla';
/** F130, §12.8: no máximo 3 recargas automáticas da TV em 10 min. */
export const TV_RELOADS_MAX = 3;
export const TV_RELOADS_WINDOW_MS = 10 * 60_000;

/** `sessionStorage` ou null quando o acesso lança (SecurityError) ou não existe. */
function session(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function read(key: string): string | null | undefined {
  const s = session();
  if (!s) return undefined;
  try {
    return s.getItem(key);
  } catch {
    return undefined;
  }
}

function write(key: string, value: string): boolean {
  const s = session();
  if (!s) return false;
  try {
    s.setItem(key, value);
    return s.getItem(key) === value;
  } catch {
    return false;
  }
}

/**
 * Trava da recarga pedida por `ops.reload_build = {build}` (§14.8): devolve true só se esta sessão ainda não recarregou
 * por esse valor E a marca foi gravada agora. Sem `sessionStorage`, devolve false: o app não recarrega (evita laço).
 */
export function claimReload(build: string): boolean {
  const key = `${RELOAD_KEY_PREFIX}${build}`;
  const current = read(key);
  if (current === undefined || current !== null) return false;
  return write(key, '1');
}

/**
 * Registra uma recarga automática da TV (§12.8): true se ainda cabe no limite (≤ 3 em 10 min) e o carimbo foi gravado;
 * false se o limite estourou ou o armazenamento falhou (a RootErrorBoundary então só reseta).
 */
export function recordTvReload(nowMs: number): boolean {
  const raw = read(TV_RELOADS_KEY);
  if (raw === undefined) return false;
  let stamps: number[] = [];
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed))
        stamps = parsed.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    } catch {
      stamps = [];
    }
  }
  const recent = stamps.filter((t) => nowMs - t >= 0 && nowMs - t < TV_RELOADS_WINDOW_MS);
  if (recent.length >= TV_RELOADS_MAX) return false;
  recent.push(nowMs);
  return write(TV_RELOADS_KEY, JSON.stringify(recent));
}

/** Atalhos de uma tecla (§15.2.7): ligados por padrão e quando o armazenamento falha; só "off" desliga. */
export function readOneKeyShortcuts(): boolean {
  return read(ONE_KEY_SHORTCUTS_KEY) !== 'off';
}

/** Grava a chave X1 (§15.2.7). Ligar remove a marca; falha é silenciosa (vale o padrão). */
export function writeOneKeyShortcuts(on: boolean): void {
  const s = session();
  if (!s) return;
  try {
    if (on) s.removeItem(ONE_KEY_SHORTCUTS_KEY);
    else s.setItem(ONE_KEY_SHORTCUTS_KEY, 'off');
  } catch {
    // modo privado ou cota cheia: vale o padrão (§2.4.4)
  }
}

// ── D3: o município guardado no aparelho (ORD-F2-022 PR "cidade"; SINAL-F2-013; FRONT-2) ─────────────────────────────
// Só o código IBGE, na chave `mapavoto.municipio`; nada sai do aparelho. Toda leitura e escrita em try/catch: no modo
// privado (ou com o armazenamento bloqueado) o site funciona igual, só não lembra.
export const MY_CITY_KEY = 'mapavoto.municipio';
const IBGE_RE = /^\d{7}$/;

function cityStore(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** O código guardado; valor fora do formato é apagado e devolve null. */
export function readMyCity(): IbgeCode | null {
  const s = cityStore();
  if (!s) return null;
  try {
    const v = s.getItem(MY_CITY_KEY);
    if (v === null) return null;
    if (IBGE_RE.test(v)) return v;
    s.removeItem(MY_CITY_KEY);
    return null;
  } catch {
    return null;
  }
}

export function writeMyCity(ibge: IbgeCode): void {
  if (!IBGE_RE.test(ibge)) return;
  try {
    cityStore()?.setItem(MY_CITY_KEY, ibge);
  } catch {
    // modo privado ou cota: o site segue sem lembrar
  }
}

export function clearMyCity(): void {
  try {
    cityStore()?.removeItem(MY_CITY_KEY);
  } catch {
    // idem
  }
}

// ── D3, nas pesquisas: a data da última visita (ORD-F2-022; §8 de 18/10 ~11h00) ───────────────────────────────────────
// Só um número (ms de época), na chave `mapavoto.pesquisas.visita` do site das pesquisas (origem própria); nada sai do
// aparelho. Serve para "{N} pesquisas novas desde a sua última visita". Mesmo acesso e mesmos cuidados do município.
export const POLLS_VISIT_KEY = 'mapavoto.pesquisas.visita';

/** A última visita guardada (ms); ausente ou ilegível, null (e o valor ruim é apagado). */
export function readPollsVisit(): number | null {
  const s = cityStore();
  if (!s) return null;
  try {
    const v = s.getItem(POLLS_VISIT_KEY);
    if (v === null) return null;
    const ms = Number(v);
    if (/^\d{10,14}$/.test(v) && Number.isFinite(ms)) return ms;
    s.removeItem(POLLS_VISIT_KEY);
    return null;
  } catch {
    return null;
  }
}

export function writePollsVisit(ms: number): void {
  if (!Number.isFinite(ms) || ms <= 0) return;
  try {
    cityStore()?.setItem(POLLS_VISIT_KEY, String(Math.round(ms)));
  } catch {
    // modo privado ou cota: a página segue sem a linha de novidades
  }
}

// ── ORD-F-153: a sessão do login da porta (equipe e convidados; docs/DECISOES.md §84) ──────────────────────────────────
// Na chave `mapavoto.sessao` do localStorage: o e-mail, o refresh token, o id token e a validade dele, em JSON. Só sai
// do aparelho para o próprio Firebase Authentication (renovação). Sem armazenamento (modo privado, bloqueado), a sessão
// fica só na memória da aba. A pré-busca do boot (src/boot/prefetch.ts) lê a mesma chave com a mesma regra de "tem
// sessão" (`parseAuthSession`; a paridade está em tests/unit/auth/session.test.ts).
export const AUTH_SESSION_KEY = 'mapavoto.sessao';

export interface AuthSession {
  email: string;
  refreshToken: string;
  idToken: string;
  /** Fim da validade do id token (ms de época, relógio do aparelho). */
  expiresAt: number;
}

/** A sessão da memória (quando o armazenamento falha); `undefined` = nunca gravada nesta aba. */
let memorySession: AuthSession | null | undefined;

/** O texto guardado → a sessão, ou null (ausente, ilegível ou sem refresh token: não há sessão). */
export function parseAuthSession(raw: string | null | undefined): AuthSession | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Record<string, unknown> | null;
    if (!v || typeof v !== 'object') return null;
    const refreshToken = v.refresh_token;
    if (typeof refreshToken !== 'string' || refreshToken === '') return null;
    const expiresAt = Number(v.expires_at);
    return {
      email: typeof v.email === 'string' ? v.email : '',
      refreshToken,
      idToken: typeof v.id_token === 'string' ? v.id_token : '',
      expiresAt: Number.isFinite(expiresAt) ? expiresAt : 0,
    };
  } catch {
    return null;
  }
}

/** A sessão guardada: a do aparelho; sem armazenamento, a da memória. */
export function readAuthSession(): AuthSession | null {
  const s = cityStore();
  if (s) {
    try {
      return parseAuthSession(s.getItem(AUTH_SESSION_KEY)) ?? memorySession ?? null;
    } catch {
      // armazenamento bloqueado na leitura: vale a memória
    }
  }
  return memorySession ?? null;
}

/** Grava a sessão (no aparelho e na memória). Devolve true se ficou no aparelho. */
export function writeAuthSession(session: AuthSession): boolean {
  memorySession = session;
  const text = JSON.stringify({
    v: 1,
    email: session.email,
    refresh_token: session.refreshToken,
    id_token: session.idToken,
    expires_at: session.expiresAt,
  });
  try {
    const s = cityStore();
    if (!s) return false;
    s.setItem(AUTH_SESSION_KEY, text);
    return s.getItem(AUTH_SESSION_KEY) === text;
  } catch {
    return false;
  }
}

/** Apaga a sessão ("Sair" ou recusa definitiva do refresh token). */
export function clearAuthSession(): void {
  memorySession = null;
  try {
    cityStore()?.removeItem(AUTH_SESSION_KEY);
  } catch {
    // modo privado: só a memória
  }
}
